import React, { Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useQuery, UseQueryResult } from "react-query";
import { getParser } from "bowser";

import { updateTheme } from "@trailhead/asurion-ui";
import { ErrorBoundaryWithDefault, RouteContainer } from "@trailhead/core-ui";
import { getChatClient, useIsChatEnabled } from "@trailhead/chat";
import { useMxContext } from "@trailhead/mx-context";
import {
  FlowTypes,
  useQueryParams,
  useSessionStorage,
} from "@trailhead/smart-setup-core";
import {
  fetchFromContentful,
  PartnerType,
  URLAlias,
  EntryCollection,
} from "@trailhead/contentful";
import { ClientPicker } from "@trailhead/setup-intro";
import { useDeviceState } from "@trailhead/global-device-context";
import { getUserBrowserId } from "@trailhead/analytics";

import { useInitialize } from "./initializations/initialize";
import { Homepage } from "./pages";

const ExpertSetupPage = React.lazy(() => import("@trailhead/expert-setup"));
const ChatOnlyPage = React.lazy(
  () => import("@trailhead/expert-setup/pages/ChatOnly"),
);
const SmartSetupIntro = React.lazy(
  () => import("@trailhead/setup-intro/pages/SmartSetupIntro"),
);
const GuidesListView = React.lazy(
  () => import("@trailhead/smart-setup/src/pages/GuidesListView"),
);
const TutorialView = React.lazy(
  () => import("@trailhead/smart-setup/src/pages/TutorialView"),
);
const SchedulerPage = React.lazy(
  () => import("@trailhead/scheduler/src/pages/Scheduler"),
);
const OzmoTutorialPage = React.lazy(
  () => import("@trailhead/ozmo/src/pages/OzmoTutorialPage"),
);

const StyledRouterContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SmartSetupRoutes: React.FC = () => {
  const { client } = useMxContext();
  const initializing = useInitialize();
  const enableMessaging = useIsChatEnabled();

  const [shouldSeePathfinderPage, setShouldSeePathfinderPage] = useState<
    boolean | null
  >(null);
  const [pathfinderVersion, setPathfinderVersion] = useState<
    string | undefined
  >("");
  const [cidPathfinder, setCIDPathfinder] = useState("");
  const [chatClientIsInitialized, setChatClientIsInitialized] = useState(false);

  const getPartnerNumber = () => {
    if (client === PartnerType.VERIZON) return "866-314-7861";
    else if (client === PartnerType.ATT) return "800-598-4070";
    else return undefined;
  };

  const extraErrorMessage = {
    message: "Call us at ",
    contactNumber: getPartnerNumber(),
  };

  useEffect(() => {
    async function fetchData() {
      if (
        client !== PartnerType.VERIZON &&
        client !== PartnerType.ASURION &&
        client !== PartnerType.ATT
      ) {
        setShouldSeePathfinderPage(false);
        return;
      }

      setShouldSeePathfinderPage(true);
    }

    fetchData();
  }, [client]);

  const autoOpenChat = false;

  const expertiseOptions = {
    newDevice: ["service-onboarding"],
    replacement: ["device-replacement-onboarding"],
  };

  const [flowType] = useSessionStorage("flowType", "");

  const path = "/mobile-device-setup";

  useEffect(() => {
    updateTheme("partner");
  }, []);

  const { search } = useLocation();
  const queryParams = useQueryParams();

  useEffect(() => {
    const showChat = async () => {
      try {
        const expertise =
          flowType === FlowTypes.NewDevice
            ? expertiseOptions.newDevice
            : expertiseOptions.replacement;
        const ChatClient = getChatClient(
          client,
          expertise[0],
          getUserBrowserId(),
        );
        await ChatClient.init(client, expertise, () => {
          setChatClientIsInitialized(true);
        });
        if (
          autoOpenChat &&
          getParser(window.navigator.userAgent).getPlatformType() === "desktop"
        )
          await ChatClient.openMessagingOverlay();
      } catch (error) {
        // TODO: send to Kibana instead of swallowing
      }
    };
    if (enableMessaging && !search.includes("cid=test")) {
      showChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, enableMessaging, expertiseOptions]);

  // TODO: for both of the useQuery calls below, replace them with useContentfulData hook
  const { data, isLoading, isError } = useQuery(
    [
      "contentfulAliases",
      {
        content_type: "urlAlias",
        "fields.partner[in]": `all,${client}`,
        limit: 1000,
      },
    ],
    fetchFromContentful,
    { staleTime: Infinity },
  );

  const landingPageCIDRedirectData: UseQueryResult<
    EntryCollection<{ cid: string; lpvariant: string; partner: string }>
  > = useQuery(
    [
      "cidRedirectToLandingPage",
      {
        content_type: "cidRedirectToLandingPage",
        "fields.partner[in]": `all,${client}`,
        limit: 1000,
      },
    ],
    fetchFromContentful,
    { staleTime: Infinity },
  );

  const cid = queryParams.get("cid");
  useEffect(() => {
    setPathfinderVersion(client === PartnerType.VERIZON ? "self-guided--unverified" : "h");
    if (
      landingPageCIDRedirectData.data?.items &&
      landingPageCIDRedirectData.data?.items.length > 0 &&
      cid
    ) {
      landingPageCIDRedirectData.data?.items.forEach(item => {
        if (
          cid.toLocaleLowerCase() === item.fields.cid.toLocaleLowerCase() &&
          item.fields.partner?.includes(client.toLocaleLowerCase())
        ) {
          setCIDPathfinder(item.fields.cid);
          setPathfinderVersion(item.fields.lpvariant);
        }
      });
    }
  }, [client, pathfinderVersion, landingPageCIDRedirectData, cid]);

  const {
    updateNewDeviceObName,
    updateOldDeviceObName,
    updateOldDeviceFunctional,
    updateNewDeviceActivated,
    updateOldDeviceAccess,
    updateDeviceFlowType,
  } = useDeviceState();

  const NavigateToIntro = () => (
    <Navigate to={`${path}/intro${search}`} replace />
  );

  const generateAlias = ({
    path: aliasPath,
    guideType,
    ...stateContext
  }: URLAlias) => {
    const NavigateWithState: React.FC = () => {
      updateNewDeviceObName(stateContext.newDevice);
      updateOldDeviceObName(stateContext.oldDevice);
      updateDeviceFlowType(
        stateContext.isReplacementDevice === true
          ? FlowTypes.Replacement
          : FlowTypes.NewDevice,
      );
      updateNewDeviceActivated(stateContext.newDeviceActivated);
      updateOldDeviceAccess(stateContext.oldDeviceAccess);
      updateOldDeviceFunctional(stateContext.oldDeviceFunctional);

      return <Navigate to={`${path}/tutorials/${guideType}`} replace />;
    };

    return <Route path={aliasPath} element={<NavigateWithState />} />;
  };

  const isVerizonSmartSetup =
    client === PartnerType.VERIZON &&
    (cid === "vz_bu_ss" || cid === "vz_su_ss");

  return (
    <Suspense fallback={<div />}>
      {!initializing &&
        !isLoading &&
        (chatClientIsInitialized || cid === "test") &&
        shouldSeePathfinderPage !== null &&
        client !== PartnerType.ASURION && (
          <>
            <StyledRouterContainer>
              <Routes>
                <Route path={`${path}/`}>
                  <Route
                    path={`tutorials/:id/:wizardId`}
                    element={
                      <ErrorBoundaryWithDefault contact={extraErrorMessage}>
                        <TutorialView />
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  <Route
                    path={`tutorials/:id`}
                    element={
                      <ErrorBoundaryWithDefault contact={extraErrorMessage}>
                        <TutorialView />
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  <Route
                    path={`tutorials`}
                    element={
                      <ErrorBoundaryWithDefault
                        contact={extraErrorMessage}
                        removeRouterWrapper={isVerizonSmartSetup}
                      >
                        <GuidesListView />
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  <Route
                    path={`intro`}
                    element={
                      <ErrorBoundaryWithDefault
                        contact={extraErrorMessage}
                        removeRouterWrapper={isVerizonSmartSetup}
                      >
                        {shouldSeePathfinderPage &&
                          !search.includes("skipPathfinder=true") && (
                            <Navigate to={`${path}/setup${search}`} replace />
                          )}
                        {(!shouldSeePathfinderPage ||
                          search.includes("skipPathfinder=true")) && (
                          <SmartSetupIntro />
                        )}
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  <Route
                    path={"setup-new"}
                    element={
                      <ErrorBoundaryWithDefault
                        removeRouterWrapper
                        contact={extraErrorMessage}
                      >
                        <Homepage />
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  <Route
                    path={`setup`}
                    element={
                      <ErrorBoundaryWithDefault
                        removeRouterWrapper={
                          pathfinderVersion === "f" ||
                          pathfinderVersion === "h" ||
                          pathfinderVersion === "i" ||
                          pathfinderVersion === "j" ||
                          pathfinderVersion === "k" ||
                          pathfinderVersion === "l" ||
                          pathfinderVersion === "self-guided--verified" ||
                          pathfinderVersion === "self-guided--unverified" ||
                          pathfinderVersion ===
                            "self-guided-with-scheduler--verified"
                        }
                        contact={extraErrorMessage}
                      >
                        {shouldSeePathfinderPage && (
                          <ExpertSetupPage
                            pathfinderVersion={pathfinderVersion}
                            cidOverride={!!cidPathfinder}
                          />
                        )}
                        {!shouldSeePathfinderPage && <NavigateToIntro />}
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  <Route
                    path={`tutorial`}
                    element={
                      <ErrorBoundaryWithDefault>
                        <OzmoTutorialPage />
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  {
                    <Route
                      path={"scheduler"}
                      element={
                        <ErrorBoundaryWithDefault removeRouterWrapper>
                          <SchedulerPage />
                        </ErrorBoundaryWithDefault>
                      }
                    />
                  }
                  <Route
                    path={"chat"}
                    element={
                      <ErrorBoundaryWithDefault>
                        <ChatOnlyPage />
                      </ErrorBoundaryWithDefault>
                    }
                  />
                  {!isError &&
                    data?.items.map(item =>
                      generateAlias(item.fields as URLAlias),
                    )}
                  <Route path="" element={<NavigateToIntro />} />
                </Route>
                <Route path="*" element={<NavigateToIntro />} />
              </Routes>
            </StyledRouterContainer>
          </>
        )}
      {!initializing &&
        !isLoading &&
        shouldSeePathfinderPage !== null &&
        client === PartnerType.ASURION && (
          <StyledRouterContainer>
            <RouteContainer>
              <Routes>
                <Route path="*" element={<ClientPicker />}></Route>
              </Routes>
            </RouteContainer>
          </StyledRouterContainer>
        )}
    </Suspense>
  );
};

export default SmartSetupRoutes;
